import LogRocket from 'logrocket';
import config from '../config';

class LogRocketService {
  private static instance: LogRocketService | null = null;
  private isInitialized = false;
  private appId: string;
  private isEnabled: boolean;

  constructor() {
    // Get LogRocket configuration from config
    this.appId = config.logRocket.appId;
    this.isEnabled = !!this.appId; // Enable if appId has a valid value
  }

  static getInstance(): LogRocketService {
    if (!LogRocketService.instance) {
      LogRocketService.instance = new LogRocketService();
    }
    return LogRocketService.instance;
  }

  /**
   * Check if LogRocket is enabled and should be initialized
   */
  get shouldInitialize(): boolean {
    return this.isEnabled && !!this.appId;
  }

  /**
   * Initialize LogRocket with error capture and session recording
   */
  initialize(): void {
    // Don't initialize if LogRocket is disabled or missing app ID
    if (this.isInitialized || !this.shouldInitialize) {
      console.log('LogRocket initialization skipped:', {
        isInitialized: this.isInitialized,
        shouldInitialize: this.shouldInitialize,
        appId: this.appId,
        isEnabled: this.isEnabled
      });
      return;
    }

    try {
      // Initialize LogRocket with full screen capture configuration
      LogRocket.init(this.appId, {
        // Enable all recording features for full screen capture
        shouldCaptureConsoleLog: true,
        shouldCaptureNetwork: true,
        shouldCaptureDom: true,
        shouldCapturePerformance: true,
        shouldCaptureScreenshot: true,

        // Domain configuration
        allowedDomains: [
          'https://pps.gsms.us',
          'as-pps-ui-dev-bah2dvbgb2hzaqbk.westus-01.azurewebsites.net',
          'https://ca-ppsgsmsprod.jollystone-2312ca8c.westus2.azurecontainerapps.io',
          'localhost',
          '127.0.0.1'
        ],

        // Network configuration
        network: {
          requestSanitizer: request => {
            // Don't record sensitive API calls
            if (request.url.includes('/api/auth') || request.url.includes('/login')) {
              return null;
            }
            return request;
          },
          responseSanitizer: response => {
            // Don't record sensitive responses
            if (response.url.includes('/api/auth') || response.url.includes('/login')) {
              return null;
            }
            return response;
          }
        },

        // Console configuration
        console: {
          isEnabled: true,
          shouldAggregateConsoleErrors: true
        },

        // DOM configuration for full screen capture
        dom: {
          isEnabled: true
        }
      });

      this.isInitialized = true;
      console.log('LogRocket initialized successfully with app ID:', this.appId);

      // Check if recording is actually working with multiple attempts
      this.checkRecordingStatus();
    } catch (error) {
      console.error('Failed to initialize LogRocket:', error);
    }
  }

  /**
   * Check LogRocket recording status with multiple attempts
   */
  private checkRecordingStatus(): void {
    const checkStatus = (attempt: number) => {
      try {
        const sessionURL = LogRocket.sessionURL;
        const isRecording = LogRocket.isRecording;

        console.log(`🔍 LogRocket Status Check (Attempt ${attempt}):`, {
          sessionURL,
          isRecording,
          timestamp: new Date().toISOString()
        });

        if (sessionURL) {
          console.log('✅ LogRocket session URL:', sessionURL);
          console.log('✅ LogRocket is recording successfully');
          return;
        }

        if (attempt < 5) {
          // Try again after 2 seconds
          setTimeout(() => checkStatus(attempt + 1), 2000);
        } else {
          console.warn('⚠️ LogRocket session URL still not available after 5 attempts');
          console.log('LogRocket object keys:', Object.keys(LogRocket));
          console.log('LogRocket object:', LogRocket);

          // Try to manually trigger recording
          try {
            LogRocket.track('manual_recording_test', { timestamp: new Date().toISOString() });
            console.log('📊 Sent test event to LogRocket');
          } catch (trackError) {
            console.error('❌ Failed to send test event:', trackError);
          }
        }
      } catch (error) {
        console.error(`Error checking LogRocket session (attempt ${attempt}):`, error);
      }
    };

    // Start checking after 1 second
    setTimeout(() => checkStatus(1), 1000);
  }

  /**
   * Identify user for session tracking
   */
  identifyUser(userInfo: any): void {
    if (!this.isInitialized || !this.shouldInitialize) {
      return;
    }

    try {
      LogRocket.identify(userInfo.userInfo.id, {
        name: userInfo.userInfo.name,
        email: userInfo.userInfo.email,
        department: userInfo.userInfo.deptId?.departmentName,
        role: userInfo.roles.pps[0].roleId.roleName,
        module: userInfo.roles.pps[0].moduleId.moduleName,
        // Add any other user metadata you want to track
        loginTime: new Date().toISOString(),
        userAgent: navigator.userAgent,
        userModulesRoles: userInfo.userModulesRoles
      });
    } catch (error) {
      console.error('Failed to identify user in LogRocket:', error);
    }
  }

  /**
   * Track custom events
   */
  trackEvent(eventName: string, properties?: any): void {
    if (!this.isInitialized || !this.shouldInitialize) {
      return;
    }

    try {
      LogRocket.track(eventName, properties);
    } catch (error) {
      console.error('Failed to track event in LogRocket:', error);
    }
  }

  /**
   * Capture custom errors
   */
  captureError(error: Error, context?: any): void {
    if (!this.isInitialized || !this.shouldInitialize) {
      return;
    }

    try {
      LogRocket.captureException(error, {
        tags: {
          component: context?.component || 'Unknown',
          action: context?.action || 'Unknown'
        },
        extra: context
      });
    } catch (logRocketError) {
      console.error('Failed to capture error in LogRocket:', logRocketError);
    }
  }

  /**
   * Get session URL for debugging
   */
  getSessionURL(): string | null {
    if (!this.isInitialized || !this.shouldInitialize) {
      return null;
    }

    try {
      return LogRocket.sessionURL;
    } catch (error) {
      console.error('Failed to get LogRocket session URL:', error);
      return null;
    }
  }

  /**
   * Clean up LogRocket
   */
  cleanup(): void {
    if (this.isInitialized) {
      try {
        // LogRocket doesn't have a shutdown method, just reset our state
        this.isInitialized = false;
      } catch (error) {
        console.error('Failed to cleanup LogRocket:', error);
      }
    }
  }

  /**
   * Check if LogRocket is currently active
   */
  get isActive(): boolean {
    return this.isInitialized && this.shouldInitialize;
  }

  /**
   * Get detailed status of LogRocket
   */
  getStatus(): any {
    try {
      return {
        isInitialized: this.isInitialized,
        isEnabled: this.isEnabled,
        appId: this.appId,
        shouldInitialize: this.shouldInitialize,
        sessionURL: LogRocket.sessionURL || null,
        isRecording: LogRocket.isRecording || false,
        logRocketObject:
          typeof LogRocket !== 'undefined' ? 'LogRocket loaded' : 'LogRocket not loaded',
        currentDomain: window.location.hostname,
        userAgent: navigator.userAgent
      };
    } catch (error) {
      return {
        error: 'Failed to get LogRocket status',
        details: error
      };
    }
  }

  /**
   * Test LogRocket functionality manually
   */
  testLogRocket(): void {
    if (!this.isInitialized) {
      console.error('LogRocket not initialized');
      return;
    }

    try {
      // Test tracking
      LogRocket.track('logrocket_test', {
        timestamp: new Date().toISOString(),
        domain: window.location.hostname,
        userAgent: navigator.userAgent
      });
      console.log('✅ Test event sent to LogRocket');

      // Test console logging
      console.log('This is a test log message for LogRocket');

      // Test error capture
      setTimeout(() => {
        try {
          throw new Error('Test error for LogRocket');
        } catch (error) {
          LogRocket.captureException(error);
          console.log('✅ Test error sent to LogRocket');
        }
      }, 1000);
    } catch (error) {
      console.error('❌ Failed to test LogRocket:', error);
    }
  }
}

export default LogRocketService;
